// Site plan + device placement for the Controls map.
//
// Geometry traced from the owner's satellite photo (north = up):
//   - The lot is a NARROW TRIANGLE pointing SOUTH: Amalfi Dr runs down the
//     east side, Upper Mesa Rd down the west, and they converge at the
//     curved southern point.
//   - The house fills the middle-east width; driveway exits EAST to Amalfi.
//   - The oval pool sits low in the lot, just above the southern point.
//   - A rectangular water feature (pond) sits just north of the house.
//   - The retaining wall runs down the WEST edge; the brushy hillface falls
//     between it and Upper Mesa Rd. The back deck notches into the middle of
//     the L-shaped footprint, west side, atop that hillface.
//   - Room naming: ISY "backroom" = North Room, ISY "room 2" = South Room
//     (adjacent to master = ISY "room 1"), ISY "room 3" = East Room
//     (adjacent to South Room).
// Interior partitions are still best-effort — refine by dragging dots in
// Arrange mode (positions persist to Firestore controlLayout).
//
// All coordinates are in the map's viewBox units (0-1000 x, 0-820 y).

export const MAP_W = 1000;
export const MAP_H = 820;

// Site shapes as point lists so the owner can drag corners in the map's
// "Lot & hill" mode (saved to controlLayout/_site, which overrides these).
//   lot  — closed polygon; triangle pointing south, curved tip approximated
//          with an extra vertex where Amalfi Dr and Upper Mesa Rd converge.
//   hill — closed polygon; brushy hillface between Upper Mesa Rd and the
//          lot's west edge.
//   wall — open polyline; retaining wall along the top of the hillface.
//   structure — closed polygon (teal); the HOUSE structure footprint. Rooms
//          should line up inside this outline.
// Re-baked 2026-08-11 evening from controlLayout/_site after the owner traced
// every shape against the aligned 2026-08 satellite photo (see
// scripts/align-satellite.py). Uniform scale: 4 viewBox units = 1 ft
// (ftPerUnit 0.25). Lot edges (clockwise from NW): 60.5 + 95.2 ft north,
// 142.5 ft along Amalfi, 38.5 ft south, 187 ft down the hill edge.
export const SITE_DEFAULTS = {
  lot: [
    { x: 192, y: 57 },
    { x: 434, y: 60 },
    { x: 815, y: 66 },
    { x: 796, y: 636 },
    { x: 643, y: 654 },
  ],
  hill: [
    { x: 210, y: 80 },
    { x: 640, y: 648 },
    { x: 586, y: 698 },
    { x: 155, y: 226 },
    { x: 174, y: 136 },
    { x: 195, y: 99 },
  ],
  wall: [
    { x: 213, y: 77 },
    { x: 615, y: 618 },
  ],
  // Traced from the roof outline in the satellite photo (L-shaped, garage
  // wing on the east).
  structure: [
    { x: 401, y: 93 },
    { x: 605, y: 96 },
    { x: 605, y: 137 },
    { x: 700, y: 140 },
    { x: 695, y: 481 },
    { x: 562, y: 480 },
    { x: 564, y: 270 },
    { x: 468, y: 268 },
    { x: 468, y: 278 },
    { x: 402, y: 278 },
  ],
};

// Rectangles below are the OWNER'S arrangement (dragged in the map's Edit
// rooms mode, re-baked from controlLayout/_zones on 2026-08-11 evening —
// now aligned to the satellite-traced structure footprint). Live edits in
// the app continue to override these — treat Firestore as the source of
// truth and re-bake here occasionally. Owner-created custom rooms
// (laundry room, hot tub, ...) live only in Firestore.
export const ZONES = [
  // ---- interior ----
  { id: 'master-bath', label: 'M. bath', x: 401, y: 96, w: 67, h: 42, type: 'room' },
  { id: 'master', label: 'Master (room 1)', x: 401, y: 139, w: 67, h: 138, type: 'room' },
  { id: 'mirror-room', label: 'Mirror room', x: 634, y: 270, w: 64, h: 48, type: 'room' },
  { id: 'hallway', label: 'Hallway', x: 468, y: 166, w: 138, h: 30, type: 'room' },
  { id: 'room2', label: 'South Rm (rm 2)', x: 471, y: 198, w: 43, h: 71, type: 'room' },
  { id: 'room3', label: 'East Rm (rm 3)', x: 517, y: 197, w: 45, h: 74, type: 'room' },
  { id: 'kitchen', label: 'Kitchen', x: 566, y: 270, w: 67, h: 47, type: 'room' },
  { id: 'entrance-bath', label: 'Entr. bath', x: 552, y: 367, w: 29, h: 26, type: 'room' },
  { id: 'backroom', label: 'North Rm (backroom)', x: 469, y: 93, w: 136, h: 73, type: 'room' },
  { id: 'sun-room', label: 'Sun room', x: 564, y: 319, w: 71, h: 32, type: 'room' },
  { id: 'side-room', label: 'Side room', x: 643, y: 385, w: 54, h: 93, type: 'room' },
  { id: 'entrance', label: 'Entrance', x: 546, y: 357, w: 29, h: 26, type: 'room' },
  { id: 'garage', label: 'Garage', x: 606, y: 140, w: 91, h: 97, type: 'room' },
  { id: 'living-room', label: 'Living room', x: 564, y: 352, w: 79, h: 130, type: 'room' },
  { id: 'bar', label: 'Bar', x: 565, y: 390, w: 31, h: 26, type: 'room' },
  // ---- outdoor ----
  { id: 'pond', label: 'Pond', x: 291, y: 157, w: 45, h: 33, type: 'outdoor' },
  { id: 'back-yard', label: 'Back yard', x: 273, y: 89, w: 95, h: 68, type: 'outdoor' },
  { id: 'deck', label: 'Back deck', x: 468, y: 271, w: 95, h: 110, type: 'outdoor' },
  { id: 'patio', label: 'Patio', x: 369, y: 95, w: 30, h: 179, type: 'outdoor' },
  { id: 'front-yard', label: 'Front yard', x: 700, y: 136, w: 104, h: 388, type: 'outdoor' },
  { id: 'pool', label: 'Pool', x: 613, y: 542, w: 120, h: 72, type: 'outdoor' },
];

// docId (controlDevices) -> zone id. Devices not listed here (and without a
// saved position) collect in the "unplaced" tray until dragged onto the map.
export const DEVICE_ZONES = {
  // master suite (ISY room1 = Master Room)
  '38_31_1D_1': 'master', // room1 entrance hall lights
  '38_35_1E_1': 'master', // room1 ceiling light
  '38_36_1C_1': 'master', // room1 desk light
  '38_1C_CA_1': 'master', // room1_closet_dim1
  '38_37_C0_1': 'master', // room1_closet_2
  '38_39_D5_1': 'master', // room1_closet_3
  '71_77_DD_1': 'master-bath', // room1_bathroom_mirror
  '71_69_AA_1': 'master-bath', // room1_bath_windows_shower
  '71_6A_CD_1': 'master-bath', // room1_bath_windows_north
  '71_69_91_1': 'master-bath', // bathroom_win_large
  // other bedrooms — ISY room2 = South Room, room3 = East Room,
  // backroom = North Room
  '41_25_99_1': 'room2',
  '41_24_8F_1': 'room3',
  '48_11_5_1': 'backroom', // backroom mid light
  '48_1D_2F_1': 'backroom', // backroom bed light
  '48_C_6D_1': 'backroom', // backroom desk light
  // halls + thermostat
  '41_12_DE_1': 'hallway', // closet hallway lights
  '48_11_7B_1': 'hallway', // bedroom hallway light
  '33_DC_D9_1': 'hallway', // thermostat main
  '33_DC_D9_2': 'hallway', // thermostat cool
  '33_DC_D9_3': 'hallway', // thermostat heat
  // living wing
  '16_2B_74_1': 'mirror-room',
  '41_19_7E_1': 'mirror-room',
  '1F_6D_69_1': 'side-room',
  '41_1E_2E_1': 'side-room',
  '3C_7E_C_1': 'sun-room',
  '3C_83_65_1': 'sun-room',
  '16_29_41_1': 'entrance', // entrance hall lights
  '3C_61_34_1': 'entrance', // entrance door chandalier
  '70_D1_4A_1': 'entrance-bath',
  '3C_7F_39_1': 'living-room',
  '24_6C_A_1': 'living-room',
  '43_1E_D6_1': 'living-room', // living room south lights
  '41_13_A2_1': 'living-room', // mantle lights
  '3C_7F_67_1': 'bar',
  '41_24_B9_1': 'kitchen',
  '41_1A_83_1': 'kitchen',
  // front / Amalfi (east) side
  '43_94_1E_1': 'front-yard', // front yard right light
  '44_E1_4C_1': 'front-yard', // front yard left light
  '4D_F2_60_1': 'front-yard', // front walkway bright
  '3C_5F_4E_1': 'front-yard', // street entrance light
  '4D_F5_99_1': 'front-yard', // runs with street lights in "night lights"
  // patio
  '3C_61_4C_1': 'patio',
  '41_23_70_1': 'patio',
  '3D_AA_7C_1': 'patio',
  '37_F0_AB_1': 'patio', // patiostrings lights
  '43_B0_38_1': 'patio', // patiostring lights
  '49_3C_CB_1': 'patio', // patio strings lights
  // back deck (west side, atop the hillface)
  '43_2B_26_1': 'deck', // deck-heater-south
  '43_2B_47_1': 'deck', // deck-heater-north
  '43_46_11_1': 'deck', // deck-heater-west
  '4D_D7_56_1': 'deck', // deck-security-light-south
  '4D_F2_D5_1': 'deck', // deck-bbq-light-sec
  '48_12_F3_1': 'deck', // trellis lights back deck
  '4A_80_58_1': 'deck', // room1 backyard sec
  '4D_F2_A8_1': 'deck', // room 1 outside patio light
  // back yard (north strip)
  '48_C_B4_1': 'back-yard', // backyard path
  '43_94_68_1': 'back-yard', // ficus tree light
  // pool (southeast corner) + pond
  '37_F0_AD_1': 'pool', // pool beach lights
  '46_4E_FF_1': 'pool', // pool reef lights
  '41_F6_2D_1': 'pool', // pool tree lights
  '43_11_16_1': 'pool', // pool patio lights
  '4D_D9_59_1': 'pool', // "pool area light" program controls it
  '31_A8_BB_1': 'pond', // drum filter waste flush pump
  '43_96_7F_1': 'pond', // drum filter manual switch
  '3F_BA_EB_1': 'pond', // skimmer flush

  // ---- inferred from the device network (2026-08-11) ----
  // These fixtures are unnamed in the ISY, so rooms were inferred from
  // scene membership and address batches. Drag any that sit wrong — a
  // dragged position overrides the zone default.
  '4D_D6_DD_1': 'deck', // "night lights amber trellis" with room-1 outside patio light
  '4D_D6_FD_1': 'deck', // same 4D.D6 outdoor batch as the trellis light (disabled in ISY)
  '4D_D8_6D_1': 'deck', // same outdoor night-light batch
  '46_4E_F4_1': 'pool', // relay one address from "pool reef lights" (46.4E.FF)
  // the "outdoor security lights" scene: 10 unnamed perimeter fixtures;
  // named scene-mates sit in the front yard (x2) and deck — parked in the
  // front yard until each is dragged to its true spot
  '4D_F2_8E_1': 'front-yard',
  '4D_F2_A0_1': 'front-yard',
  '4D_F3_A7_1': 'front-yard',
  '4D_F3_DF_1': 'front-yard',
  '4D_F3_ED_1': 'front-yard',
  '4D_F3_F5_1': 'front-yard',
  '4D_F5_1F_1': 'front-yard',
  '4D_F5_30_1': 'front-yard',
  '4D_F5_72_1': 'front-yard',
  '4D_F5_84_1': 'front-yard',
};

// Deterministic default spot inside the zone: devices spread on a small grid
// in name order, so dots don't stack before the owner arranges them. Takes
// the EFFECTIVE zone rect (defaults may be overridden by the owner's saved
// room layout in Firestore controlLayout/_zones).
export function defaultPosition(zone, indexInZone, zoneCount) {
  if (!zone) return null;
  const cols = Math.max(1, Math.ceil(Math.sqrt(zoneCount)));
  const rows = Math.max(1, Math.ceil(zoneCount / cols));
  const col = indexInZone % cols;
  const row = Math.floor(indexInZone / cols);
  // Narrow zones (the hallway strip) can't afford the full inset.
  const pad = Math.min(16, zone.w / 4, zone.h / 4);
  const x = zone.x + pad + ((col + 0.5) * (zone.w - pad * 2)) / cols;
  const y = zone.y + pad + ((row + 0.5) * (zone.h - pad * 2)) / rows;
  return { x, y };
}
